import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import './DocumentModal.css';
import { PDFDocument } from 'pdf-lib';
import { customDocuments, allDocuments } from '../../../utils/documentConfigs';
import { getInitialDocumentStates } from '../../../utils/documentHelpers';

const DocumentModal = ({ show, onHide, vehiculeId = null }) => {
    const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5002/api";
    
    const [isLoading, setIsLoading] = useState(false);
    const [commonData, setCommonData] = useState(null);
    const [selectedDocuments, setSelectedDocuments] = useState(getInitialDocumentStates());
    const resetSelections = () => setSelectedDocuments(getInitialDocumentStates());

    useEffect(() => {
        const fetchVehicleData = async () => {
            if (!vehiculeId) return;
            try {
                const res = await fetch(`${API_URL}/vehicules/${vehiculeId}`, {
                    headers: { Authorization: `Bearer ${localStorage.getItem("token")}` }
                });
                const json = await res.json();
                setCommonData(json);
            } catch (err) {
                console.error("Erreur récupération véhicule:", err);
            }
        };
        fetchVehicleData();
    }, [vehiculeId]);


    useEffect(() => {
        if (show) {
            document.body.classList.add('modal-open');
        } else {
            document.body.classList.remove('modal-open');
        }
        
        return () => {
            document.body.classList.remove('modal-open');
        };
    }, [show]);

    const handleDocumentChange = (documentKey) => {
        setSelectedDocuments({
            ...selectedDocuments,
            [documentKey]: !selectedDocuments[documentKey]
        });
    };

    const handleDownload = async () => {
        if (!vehiculeId) {
            alert("Aucun véhicule sélectionné. Veuillez d'abord sélectionner un véhicule.");
            return;
        }

        const selectedDocKeys = Object.keys(selectedDocuments).filter(key => selectedDocuments[key]);
        if (selectedDocKeys.length === 0) {
            alert("Veuillez sélectionner au moins un document à télécharger");
            return;
        }

        setIsLoading(true);
        const token = localStorage.getItem("token");

        try {
            const cerfaTypes = selectedDocKeys
            .map(key => allDocuments.find(doc => doc.key === key)?.cerfa?.replace("*", "-"))
            .filter(Boolean);


            const blobs = [];

            // Générer CERFA
            if (cerfaTypes.length > 0) {
                const res = await fetch(`${API_URL}/pdfs/generate-pdf`, {
                    method: "POST",
                    headers: {
                        Authorization: `Bearer ${token}`,
                        "Content-Type": "application/json"
                    },
                    body: JSON.stringify({
                        selectedVehicles: [parseInt(vehiculeId)],
                        cerfa_types: cerfaTypes
                    })
                });

                if (!res.ok) throw new Error("Erreur génération CERFA");
                blobs.push(await res.blob());
            }

            // Générer les documents personnalisés
            const documentConfigs = customDocuments(vehiculeId, commonData);
            const selectedConfigs = documentConfigs.filter(doc => selectedDocKeys.includes(doc.key));

            for (const doc of selectedConfigs) {
                for (const groupId of doc.groups) {
                    try {
                        const resId = await fetch(`${API_URL}/documents/resolve-id?groupId=${groupId}&filename=${doc.filename}`, {
                            headers: { Authorization: `Bearer ${token}` }
                        });
                        const { documentId } = await resId.json();

                        const res = await fetch(`${API_URL}/documents/generate`, {
                            method: "POST",
                            headers: {
                                Authorization: `Bearer ${token}`,
                                "Content-Type": "application/json"
                            },
                            body: JSON.stringify({
                                groupId,
                                documentId,
                                data: doc.getData()
                            })
                        });

                        if (!res.ok) continue;

                        blobs.push(await res.blob());
                        break;
                    } catch (err) {
                        console.warn(`Erreur génération ${doc.key} (group ${groupId})`, err);
                    }
                }
            }

            // Fusion PDF
            const mergedPdf = await PDFDocument.create();
            for (const blob of blobs) {
                const buffer = await blob.arrayBuffer();
                const pdf = await PDFDocument.load(buffer);
                const pages = await mergedPdf.copyPages(pdf, pdf.getPageIndices());
                pages.forEach(page => mergedPdf.addPage(page));
            }

            const finalBytes = await mergedPdf.save();
            const finalBlob = new Blob([finalBytes], { type: 'application/pdf' });
            const url = URL.createObjectURL(finalBlob);

            const a = document.createElement('a');
            a.href = url;
            a.download = "Documents_vehicule.pdf";
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            window.open(url, "_blank");

            resetSelections();
            onHide();

        } catch (error) {
            console.error("Erreur lors du téléchargement:", error);
            alert("Erreur lors du téléchargement des documents. Veuillez réessayer.");
        } finally {
            setIsLoading(false);
        }
    };

    if (!show) {
        return null;
    }

    const isVehicleSelected = !!vehiculeId;

    return (
        show && (
            <div className="document-modal-overlay" onClick={(e) => {
                if (e.target.className === 'document-modal-overlay') {
                    onHide();
                }
            }}>
                <div className="document-modal">
                    <div className="document-modal-header">
                        <h2>Télécharger un document</h2>
                        <button className="close-button" onClick={onHide}>×</button>
                    </div>
                    
                    <div className="document-modal-body">
                        {!isVehicleSelected && (
                            <div className="document-warning">
                                <p className="document-warning-text">
                                    ⚠️ Aucun véhicule sélectionné. Veuillez d&apos;abord sélectionner un véhicule.
                                </p>
                            </div>
                        )}
                        
                        <p className="document-instructions">
                            Sélectionnez les documents CERFA que vous souhaitez télécharger :
                        </p>
                        
                        <div className="documents-list">
                            {allDocuments
                                .filter(doc => {
                                    // Toujours visible si cerfa, sinon on filtre par groupe
                                    if (doc.cerfa) return true;
                                    const groupId = commonData?.concession?.group_id || commonData?.concession_groupe_id;
                                    return doc.groups?.includes(Number(groupId));
                                })
                                .map(({ key, label, cerfa }) => (
                                    <div className="document-item" key={key}>
                                    <input
                                        type="checkbox"
                                        id={key}
                                        name={key}
                                        checked={selectedDocuments[key] || false}
                                        onChange={() => handleDocumentChange(key)}
                                    />
                                    <label htmlFor={key}>
                                        {label}
                                        {cerfa && <span className="cerfa-number">(Cerfa {cerfa})</span>}
                                        <span className="not-available"></span>
                                    </label>
                                    </div>
                                ))
                            }
                        </div>
                    </div>
                    
                    <div className="modal-footer">
                        <button type="button" className="cancel-button" onClick={onHide} disabled={isLoading}>Annuler</button>
                        <button 
                            type="button"
                            className="download-button" 
                            onClick={handleDownload} 
                            disabled={isLoading || !isVehicleSelected}
                        >
                            {isLoading ? "Génération en cours..." : "Télécharger"}
                        </button>
                    </div>
                </div>
            </div>
        )
    );
};

DocumentModal.propTypes = {
    show: PropTypes.bool.isRequired,
    onHide: PropTypes.func.isRequired,
    vehiculeId: PropTypes.oneOfType([
        PropTypes.number,
        PropTypes.string
    ])
};

export default DocumentModal;